import { useAppDispatch, useAppSelector } from 'hooks';
import { selectIsCurrentTabPrimary } from 'persistence/persistenceBetweenTabs';
import { useCallback, useEffect, useRef } from 'react';
import {
  finishDelayingNextMessage,
  selectDelayingMessage,
  selectNextMessageToDelay,
  selectShouldSynchronizeMessageDelays,
  startDelayingNextMessage,
  synchronizeMessageDelays,
} from 'reducers/workflows';

export function useArtificialMessageDelay() {
  const dispatch = useAppDispatch();
  const shouldSynchronizeMessageDelays = useAppSelector(
    selectShouldSynchronizeMessageDelays,
  );
  const nextMessageToDelay = useAppSelector(selectNextMessageToDelay);
  const delayingMessage = useAppSelector(selectDelayingMessage);
  const isCurrentTabPrimary = useAppSelector(selectIsCurrentTabPrimary);

  const timeout = useRef<NodeJS.Timeout>();

  const scheduleDelayEnd = useCallback(
    (delay: number) => {
      timeout.current = setTimeout(() => {
        timeout.current = undefined;
        dispatch(finishDelayingNextMessage(Date.now()));
      }, delay);
    },
    [dispatch],
  );

  useEffect(() => {
    // If the current tab is not primary, clear timeout and don't do anything,
    // since this tab will be receiving state from the primary tab:
    if (!isCurrentTabPrimary) {
      clearTimeout(timeout.current);
      timeout.current = undefined;

      return;
    }

    if (shouldSynchronizeMessageDelays) {
      dispatch(synchronizeMessageDelays());

      return;
    }

    if (delayingMessage) {
      // If delay end is already scheduled, all good, don't do anything:
      if (timeout.current) {
        return;
      }

      // If delay end is not scheduled, that means this is a new tab that
      // was opened when a delay was in progress.
      // Schedule delay end for the currently delaying message:
      scheduleDelayEnd(delayingMessage.delay);

      return;
    }

    if (nextMessageToDelay) {
      dispatch(startDelayingNextMessage());
      scheduleDelayEnd(nextMessageToDelay.delay);
    }
  }, [
    dispatch,
    nextMessageToDelay,
    delayingMessage,
    shouldSynchronizeMessageDelays,
    isCurrentTabPrimary,
    scheduleDelayEnd,
  ]);
}
